import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  AlertTriangle,
  RefreshCw,
  XCircle,
  Check,
  Heart,
  MessageSquare,
  Shield,
  Zap,
  BookOpen,
  BarChart3,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { billingApi } from '@/services/billingApi';
import type { Subscription } from '@/types/billing';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CANCELLATION_REASONS = [
  { id: 'too_expensive', label: 'Too expensive', icon: BarChart3 },
  { id: 'not_using', label: 'Not using it enough', icon: BookOpen },
  { id: 'found_alternative', label: 'Found an alternative', icon: Zap },
  { id: 'missing_features', label: 'Missing features I need', icon: Shield },
  { id: 'other', label: 'Other', icon: MessageSquare },
] as const;

const FEATURES_YOULL_LOSE = [
  'Personalized AI coaching sessions',
  'Custom learning plans and daily content',
  'Skill assessments and progress tracking',
  'Access to all lessons, tips, and resources',
  'Priority support and community access',
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CancelSubscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [feedback, setFeedback] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await billingApi.getActiveSubscription(user.id);
      setSubscription(data);

      if (data.status === 'CANCELLED') {
        setIsCancelled(true);
      }
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? '';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      if (message.includes('404') || message.toLowerCase().includes('not found')) {
        setSubscription(null);
      } else {
        setError(message || 'Failed to load subscription');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, navigate, location.pathname]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleCancelClick = () => {
    if (!selectedReason) {
      toast({
        title: 'Please select a reason',
        description: 'We would appreciate knowing why you are leaving.',
        variant: 'destructive',
      });
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!subscription) return;
    try {
      setIsSubmitting(true);
      const reasonLabel =
        CANCELLATION_REASONS.find((r) => r.id === selectedReason)?.label ?? selectedReason;
      await billingApi.cancelSubscription(subscription.id, {
        reason: reasonLabel,
        feedback: feedback.trim() || undefined,
      });
      setIsCancelled(true);
      setShowConfirmDialog(false);
      toast({
        title: 'Subscription cancelled',
        description: 'Your subscription has been cancelled.',
      });
    } catch (err: unknown) {
      toast({
        title: 'Cancellation failed',
        description: (err as Error)?.message ?? 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <BrandedLoading message="Loading subscription…" iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Error ----
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <div className="text-center space-y-4 max-w-md">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchSubscription}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---- No subscription ----
  if (!subscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="py-8 pt-20 sm:py-12 sm:pt-24">
          <div className="container max-w-2xl mx-auto px-4 sm:px-6 text-center space-y-4">
            <h1 className="text-2xl font-bold">No Active Subscription</h1>
            <p className="text-muted-foreground">You don't have a subscription to cancel.</p>
            <Button onClick={() => navigate('/billing')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Billing
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Cancellation success state ----
  if (isCancelled) {
    const accessEnd = subscription.endDate ?? subscription.currentPeriodEnd;
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="py-8 pt-20 sm:py-12 sm:pt-24">
          <div className="container max-w-2xl mx-auto px-4 sm:px-6 space-y-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Card>
                <CardContent className="pt-8 pb-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h1 className="text-2xl font-bold">Subscription Cancelled</h1>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Your subscription has been cancelled. We're sorry to see you go.
                  </p>
                  {accessEnd && (
                    <div className="rounded-lg bg-muted/50 border p-4 max-w-sm mx-auto">
                      <p className="text-sm text-muted-foreground">You still have access until</p>
                      <p className="text-lg font-semibold">{formatDate(accessEnd)}</p>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          await billingApi.reactivateSubscription(subscription.id);
                          toast({
                            title: 'Subscription reactivated',
                            description: 'Welcome back!',
                          });
                          navigate('/billing');
                        } catch (err: unknown) {
                          toast({
                            title: 'Reactivation failed',
                            description: (err as Error)?.message ?? 'Please try again.',
                            variant: 'destructive',
                          });
                        }
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reactivate Subscription
                    </Button>
                    <Button variant="ghost" onClick={() => navigate('/billing')}>
                      Back to Billing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Main cancellation form ----
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-2xl mx-auto px-4 sm:px-6 space-y-6">
          {/* Back nav */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Button variant="ghost" size="sm" onClick={() => navigate('/billing')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Billing
            </Button>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <Heart className="h-12 w-12 text-rose-400 mx-auto" />
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              We're sorry to see you go
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Before you cancel, we'd love to understand why so we can improve.
            </p>
          </motion.div>

          {/* Reason selection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Why are you cancelling?</CardTitle>
                <CardDescription>Select the primary reason</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {CANCELLATION_REASONS.map((reason) => {
                  const Icon = reason.icon;
                  const isSelected = selectedReason === reason.id;
                  return (
                    <label
                      key={reason.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/30 hover:bg-muted/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="cancellation-reason"
                        value={reason.id}
                        checked={isSelected}
                        onChange={() => setSelectedReason(reason.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          isSelected ? 'border-primary' : 'border-muted-foreground/40'
                        }`}
                      >
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium">{reason.label}</span>
                    </label>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>

          {/* Feedback */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Additional Feedback</CardTitle>
                <CardDescription>Optional - help us improve</CardDescription>
              </CardHeader>
              <CardContent>
                <Label htmlFor="feedback" className="sr-only">
                  Feedback
                </Label>
                <textarea
                  id="feedback"
                  className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                  placeholder="Tell us more about your experience..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {feedback.length}/1000
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* What you'll lose */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-5 w-5" />
                  What you'll lose
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {FEATURES_YOULL_LOSE.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <XCircle className="h-4 w-4 text-amber-600 dark:text-amber-500 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-8"
          >
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => navigate('/billing')}
            >
              <Check className="h-4 w-4 mr-2" />
              Keep My Subscription
            </Button>
            <Button
              variant="destructive"
              className="w-full sm:w-auto"
              onClick={handleCancelClick}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Cancel Subscription
            </Button>
          </motion.div>
        </div>
      </main>
      <Footer />

      {/* Final confirmation dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block">
                Your subscription will be cancelled. You will continue to have access until the end of
                your current billing period.
              </span>
              {(subscription.endDate || subscription.currentPeriodEnd) && (
                <span className="block font-medium">
                  Access ends: {formatDate(subscription.endDate ?? subscription.currentPeriodEnd ?? '')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isSubmitting}
            >
              Go Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <BrandedSpinner className="mr-1" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CancelSubscriptionPage;
